import { ConfigProvider, message, notification } from "antd";
import { Outlet, useLocation } from "react-router-dom";
import styled from "styled-components";
import IndustrialSiteNav from "../components/marketing/IndustrialSiteNav";
import { NavFooter } from "../components";
import { AppLayout, ContentLayout } from "./Layout";
import OffscreenTrigger from "../components/OffscreenTrigger";
import AuthProvider from "../context/AuthProvider";
import ScrollOffscreenProvider from "../context/ScrollOffscreenProvider";
import BackLocationsProvider from "../context/BackLocationsProvider";
import { MobileWidgetProvider } from "../context/MobileWidgetContext";
import { useEffect } from "react";
import ContentGate from "../components/ContentGate";
import SignupModal from "../components/login/SignupModal";

import { APP_NAME } from "../helpers/constants";
import { showHeaderUi, showFooterUi } from "../helpers/general";
import {
  trackActivityEvent,
  installGlobalActivityListeners,
} from "../lib/analytics/trackActivityEvent";
import { Helmet } from "react-helmet";
import { Spacer } from "../styles/CommonElements";
import { device } from "../lib/breakpoints";
import PostLoginRedirect from "../components/PostLoginRedirect";
import { WidgetProvider } from "../context/WidgetContext";
import FeedbackWidgetWrapper from "../components/FeedbackWidgetWrapper";
// Light theme following Clumse branding guide
export const antdLightTheme = {
  token: {
    fontFamily:
      "'Inter', 'Inter Tight', system-ui, ui-sans-serif, sans-serif",
    colorPrimary: "var(--color-primary)",
    colorBgContainer: "var(--color-bg-secondary)",
    colorBgElevated: "var(--color-bg-secondary)",
    colorBgLayout: "var(--color-bg-primary)",
    colorText: "var(--color-text)",
    colorTextSecondary: "var(--color-text-light)",
    colorTextTertiary: "var(--color-text-muted)",
    colorBorder: "var(--color-border-light)",
    colorBorderSecondary: "var(--color-border-medium)",
    borderRadius: 12,
    boxShadow: "none",
    boxShadowSecondary: "none",
  },
  components: {
    Layout: {
      bodyBg: "var(--color-bg-primary)",
      headerBg: "var(--color-bg-secondary)",
      siderBg: "var(--color-bg-secondary)",
    },
    Card: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorderSecondary: "var(--color-border-light)",
      borderRadius: 16,
      boxShadow: "none",
    },
    Table: {
      colorBgContainer: "var(--color-bg-secondary)",
      headerBg: "var(--color-bg-tertiary)",
      headerColor: "var(--color-text)",
      rowHoverBg: "var(--color-bg-tertiary)",
      borderColor: "var(--color-border-light)",
    },
    Button: {
      borderRadius: 12,
      primaryShadow: "0 4px 18px var(--shadow-medium)",
      defaultShadow: "0 2px 8px var(--shadow-light)",
    },
    Input: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border-light)",
      borderRadius: 8,
    },
    Select: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border-light)",
      borderRadius: 8,
      // Ensure arrow is vertically centered
      controlHeight: 48,
    },
    Modal: {
      colorBgElevated: "var(--color-bg-secondary)",
      borderRadius: 16,
      boxShadow: "none",
    },
    Popover: {
      colorBgElevated: "var(--color-bg-secondary)",
      borderRadius: 12,
      boxShadow: "none",
    },
    Notification: {
      colorBgElevated: "var(--color-bg-secondary)",
      colorText: "var(--color-text)",
      borderRadius: 12,
      boxShadow: "none",
    },
    Message: {
      colorBgElevated: "#ffffff",
      borderRadius: 12,
      boxShadow: "0 7px 16px rgba(22, 24, 31, 0.18)",
      colorText: "var(--color-text)",
    },
    Skeleton: {
      colorFill: "var(--color-bg-tertiary)",
      colorFillContent: "var(--color-border-light)",
    },
    Menu: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorItemBg: "transparent",
      colorItemBgSelected: "var(--color-primary-glow)",
      colorItemText: "var(--color-text)",
      colorItemTextSelected: "var(--color-primary)",
      borderRadius: 8,
    },
    Dropdown: {
      colorBgElevated: "var(--color-bg-secondary)",
      borderRadius: 12,
      boxShadow: "none",
    },
    Tooltip: {
      colorBgSpotlight: "var(--color-text)",
      borderRadius: 8,
      // Disable tooltips on mobile
      ...(typeof window !== "undefined" &&
        window.innerWidth <= 768 &&
        {
          // On mobile, tooltips won't show
        }),
    },
    DatePicker: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border)",
      colorText: "var(--color-text)",
      colorTextPlaceholder: "var(--color-text-light)",
      borderRadius: 8,
      boxShadow: "none",
    },
    Calendar: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border)",
      colorText: "var(--color-text)",
      borderRadius: 8,
      boxShadow: "none",
    },
  },
};

// Global toast + notification timing (see frontend/docs/BRANDING_GUIDE.md)
message.config({
  duration: 5,
  maxCount: 3,
  rtl: false,
  // Always attach to body so fixed positioning is relative to the viewport (full
  // page), not a transformed / inset ancestor like the full-bleed chat shell.
  getContainer: () => document.body,
});

notification.config({
  placement: "top",
  duration: 5,
  getContainer: () => document.body,
});

export default function MainLayout() {
  const location = useLocation();
  const isHeaderVisible = showHeaderUi(location);
  const isFooterVisible = showFooterUi(location);
  const isFullBleedChat =
    location.pathname === "/chat" || location.pathname.startsWith("/chat/");

  useEffect(() => {
    installGlobalActivityListeners();
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
    trackActivityEvent("page_view");
  }, [location.pathname, location.search]);

  return (
    <ConfigProvider theme={antdLightTheme}>
      <Helmet>
        <title>{APP_NAME}</title>
        <link rel="icon" href={`/favicon-${APP_NAME.toLowerCase()}.ico`} />
      </Helmet>
      <AuthProvider>
        <PostLoginRedirect />
        <BackLocationsProvider>
          <ScrollOffscreenProvider>
            <MobileWidgetProvider>
              <WidgetProvider>
                <AppLayout>
                  <Container
                    $headerVisible={isHeaderVisible}
                    $fullBleedChat={isFullBleedChat}
                  >
                    {isHeaderVisible && <IndustrialSiteNav />}
                    <ContentLayout>
                      <Outlet />
                    </ContentLayout>
                    {isFooterVisible && <NavFooter />}
                  </Container>
                  <OffscreenTrigger />
                </AppLayout>
                <FeedbackWidgetWrapper />
                <ContentGate />
                <SignupModal />
              </WidgetProvider>
            </MobileWidgetProvider>
          </ScrollOffscreenProvider>
        </BackLocationsProvider>
      </AuthProvider>
    </ConfigProvider>
  );
}

const Container = styled.div`
  /* Full-page Clumse chat uses fixed layout below the nav; skip outlet padding so
     the chat surface can align to the viewport (Gemini-style). */
  padding-top: ${(props) =>
    props.$fullBleedChat
      ? "0"
      : props.$headerVisible
        ? "var(--navbar-height)"
        : "0"};
  box-sizing: border-box;
  /* Match the fixed nav so the reserved strip is never the ant layout / body white. */
  background-color: ${(props) =>
    props.$headerVisible && !props.$fullBleedChat
      ? "#000000"
      : "transparent"};

  @media ${device.mobile} {
    padding-top: ${(props) =>
      props.$fullBleedChat
        ? "0"
        : props.$headerVisible
          ? "var(--navbar-height-mobile)"
          : "0"};
  }

  .ant-menu-item {
    margin-bottom: 14px !important;
  }
`;
